import { type LoginThrottleStatusDTO, LoginThrottleStatusParams, ResetLoginThrottleParams } from "@pentacode/core";
import { css, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { live } from "lit/directives/live.js";
import { app } from "../../init";
import { alert, confirm } from "../alert-dialog";
import { Dialog } from "../dialog";
import { describeLoginThrottleStatus } from "./login-throttle-dialog.status";

/** Admin-only: look up and clear the failed-login counter for an email. */
@customElement("ptc-login-throttle-dialog")
export class LoginThrottleDialog extends Dialog<{ email?: string }, void> {
    readonly preventDismiss = true;

    @state()
    private _email = "";

    @state()
    private _status: LoginThrottleStatusDTO | null = null;

    static styles = [
        ...Dialog.styles,
        css`
            .inner {
                padding: 1em;
            }

            .actions > :not(:last-child) {
                margin-right: 0.7em;
            }

            .field input {
                width: 100%;
                box-sizing: border-box;
            }

            .hint {
                margin: 0.5em 0 1em;
            }

            .status {
                min-height: 1.5em;
                margin-bottom: 1em;
            }
        `,
    ];

    async show(input: { email?: string } = {}) {
        this._email = input.email ?? "";
        this._status = null;
        return super.show(input);
    }

    renderContent() {
        return html`
            <h3>Anmeldesperre zurücksetzen</h3>

            <div class="field">
                <label>E-Mail-Adresse</label>
                <input
                    type="email"
                    name="email"
                    placeholder="name@beispiel.de"
                    .value=${live(this._email)}
                    @input=${(e: Event) => {
                        this._email = (e.target as HTMLInputElement).value.trim();
                        this._status = null;
                    }}
                />
            </div>

            <p class="hint subtle small">
                Das Zurücksetzen löscht nur den Zähler der Fehlversuche, das Passwort bleibt unverändert und es wird
                kein Zugriff gewährt.
            </p>

            <p class="status text-centering">${this._status ? describeLoginThrottleStatus(this._status).text : ""}</p>

            <div class="horizontal layout actions">
                <button class="stretch" type="button" @click=${this._check} ?disabled=${!this._email}>
                    Status prüfen
                </button>
                <button
                    class="primary stretch"
                    type="button"
                    @click=${this._reset}
                    ?disabled=${!this._status || !describeLoginThrottleStatus(this._status).resettable}
                >
                    Sperre zurücksetzen
                </button>
                <button class="stretch" type="button" @click=${() => this.done()}>Abbrechen</button>
            </div>
        `;
    }

    private async _check() {
        this.loading = true;
        try {
            this._status = await app.api.getLoginThrottleStatus(new LoginThrottleStatusParams({ email: this._email }));
        } catch (e) {
            this.hide();
            await alert(e.message, { type: "warning" });
            void this.show({ email: this._email });
        }
        this.loading = false;
    }

    private async _reset() {
        const email = this._email;
        if (
            !(await confirm(`Willst du die Anmeldesperre für ${email} zurücksetzen?`, "Zurücksetzen", "Abbrechen", {
                title: "Anmeldesperre zurücksetzen",
                type: "question",
            }))
        ) {
            return;
        }

        this.loading = true;
        try {
            await app.api.resetLoginThrottle(new ResetLoginThrottleParams({ email }));
            this.loading = false;
            this.done();
            await alert("Anmeldesperre wurde zurückgesetzt.", { type: "success" });
        } catch (e) {
            this.loading = false;
            this.hide();
            await alert(e.message, { type: "warning" });
            void this.show({ email });
        }
    }
}
